/* eslint-disable */
// @ts-nocheck
import * as React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import classNames from 'classnames';
import Title from './Title';
import type { TitleProps } from '../../types/titleTypes';
import type { ChangeInputEvent } from '../../types/inputTypes';
// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input
const VALID_INPUT_PROPS = [
  'accept',
  'autoComplete',
  'autoFocus',
  'capture',
  'checked',
  'disabled',
  'form',
  'formAction',
  'formEnctype',
  'formMethod',
  'formNoValidate',
  'formTarget',
  'height',
  'incremental',
  'inputMode',
  'key',
  'list',
  'max',
  'min',
  'multiple',
  'name',
  'onFocus',
  'onBlur',
  'pattern',
  'placeholder',
  'readOnly',
  'required',
  'selectionDirection',
  'size',
  'spellCheck',
  'src',
  'step',
  'tabIndex',
  'type',
  'webkitDirectory',
  'width',
];
// Please use instead the relevant core components for the following types:
// TODO: Add 'file' to this list, in order to only
// allow only ImageUploader component to apply type="file"
const INVALID_TYPES = ['button', 'checkbox', 'radio'];
type Props = React.HTMLProps<HTMLInputElement> & {
  /**
   * required id for automation testing
   */
  testid: string;

  /**
   * Component to replace the input tag
   */
  customChildComponent?: React.ReactElement<any>;

  /**
   * Append classes to input container
   */
  className?: string;

  /**
   * Additonal note to display below the input field ('description' goes above)
   * Please see renderFootnote for further clarification.
   */
  footnote?: string | React.ReactElement<any>;

  /**
   * Label for mixpanel tracking
   * NOTE: If no text 'label' prop is passed in, you need to pass
   *       either an i13nLabel or i13nModel.
   */
  i13nLabel?: string;

  /**
   * Model passed into the mixpanel event. (Not necessary for most use cases).
   * label attribute can be overridden by i13nLabel.
   *
   * Order of label precendence:
   * 1. i13nLabel
   * 2. i13nModel.label
   * 3. [MOST COMMON] If the label is text, we will default to it.
   */
  i13nModel?: {
    label: string;
    params: {};
  };

  /**
   * Append classes to input element
   */
  inputClassName?: string;

  /**
   * Maxium input length
   */
  maxLength?: number;

  /**
   * Minimum input length
   */
  minLength?: number;

  /**
   * Ensures no extra space left below the input field
   */
  noFooter?: boolean;

  /**
   * callback to parent on input text change
   * required in all scenarios EXCEPT when the input field is always disabled
   * and therefore cannot change
   */
  onChange?: (event: ChangeInputEvent) => void;

  /**
   * callback to parent on key press
   */
  onKeyPress?: (e: React.SyntheticEvent<HTMLInputElement>) => void;

  /**
   * callback to parent on input going out of focus
   */
  onBlur?: (e: React.SyntheticEvent<HTMLInputElement>) => void;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Determines color of input field, as well as whether to display
   * the corresponding statusFootnote or not.
   */
  statusType?: 'error' | 'warning' | 'success';

  /**
   * If passed in AND statusType is passed in, the corresponding message will
   * display as a footer. Please see renderFootnote for further clarification.
   */
  statusFootnotes?:
    | {
        error?: string | null | undefined;
        warning?: string | null | undefined;
        success?: string | null | undefined;
      }
    | null
    | undefined;

  /**
   * Input type, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input
   * NOTE: Cannot be one of the INVALID_TYPES (button, checkbox, or radio)
   */
  type?: string;

  /**
   * Allows you to pass in an optional message to show in case of
   * validation error rather than showing the browser default.
   * Please see renderFootnote for further clarification.
   */
  validationMessage?: string | null | undefined;
} & TitleProps;
type State = {
  value: string | null | undefined;
  error: boolean | null | undefined;
};

class Input extends React.PureComponent<Props, State> {
  static defaultProps = {
    type: 'text',
    onChange: () => {},
  };

  static contextTypes = {
    i13n: PropTypes.object,
  };

  state = {
    value: '',
    error: null,
  };

  constructor(props: Props) {
    super(props);
    const labelIsText = props.label && typeof props.label === 'string';

    if (!props.i13nLabel && !props.i13nModel && !labelIsText) {
      throw new Error('Either a text label, i13nLabel, or i13nModel must be passed to the Input component.');
    }

    if (R.contains(props.type, INVALID_TYPES)) {
      throw new Error(
        'Input component is not meant for buttons, checkboxes, or radios. Please use the corresponding core component instead.'
      );
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const { value } = nextProps;
    this.setState({
      value: value === undefined || value === null ? '' : value,
    });
    // yield time after setState
    setTimeout(() => {
      this.validate();
    });
  }

  componentDidMount() {
    const { value } = this.props;
    this.setState({
      value: value || '',
    });
  }

  /**
   * validate input using form validation
   * https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/HTML5/Constraint_validation
   * http://caniuse.com/#feat=form-validation
   */
  validate = () => {
    const { maxLength, minLength } = this.props;
    const input = this.refs.input;
    let isValid = true;

    if (!input) {
      return;
    }

    if (maxLength) {
      // custom handler as webkit will not allow to type more than maxLength
      const errorTooLong = `Maximum input length is ${maxLength} characters.`;

      if (maxLength <= input.value.length) {
        // perform custom length error message
        input.setCustomValidity && input.setCustomValidity(errorTooLong);
      } else {
        input.setCustomValidity && input.setCustomValidity('');
      }
    }

    if (minLength) {
      const errorTooShort = `Minimum input length is ${minLength} characters.`;

      if (minLength > input.value.length) {
        input.setCustomValidity && input.setCustomValidity(errorTooShort);
      } else {
        input.setCustomValidity && input.setCustomValidity('');
      }
    }

    isValid = input.checkValidity && input.checkValidity();
    this.setState({
      error: !isValid,
    });
  };

  fireMixpanelEvent = (value: string) => {
    if (!this.context.i13n) {
      return;
    }

    // Set defaults
    let i13nModel = this.props.i13nModel || {};
    let label = this.props.label || '';

    if (!label || typeof label !== 'string') {
      label = this.props.i13nLabel || i13nModel.label || '';
    }

    // Merge params
    const params = R.merge(
      {
        component: 'Input',
        value,
      },
      i13nModel.params
    );
    // Final merge in case there are other properties passed besides label and params
    i13nModel = R.merge(i13nModel, {
      label,
      params,
    });

    if (this.props.type === 'password') {
      i13nModel.params.value = 'Password changed';
    }

    this.context.i13n.executeEvent('log', i13nModel);
  };

  onChange = (e: {
    target: {
      value: string;
    };
  }) => {
    const target = e.target;
    this.setState({
      value: target.value,
    });
    // Yield thread for setState
    setTimeout(() => {
      this.validate();
      // TODO: fire Mixpanel events, with a debounce
      // this.fireMixpanelEvent(target.value);
      this.props.onChange &&
        this.props.onChange({
          target,
          value: this.state.value || '',
          error: this.state.error,
        });
    }, 0);
  };

  /**
   * public interface to get current input value
   * @return {String} input value
   */
  getValue = () => {
    return this.state.value;
  };

  /**
   * public interface to check input validation
   */
  isInputValid = (): boolean => {
    return this.refs.input && this.refs.input.checkValidity && this.refs.input.checkValidity();
  };

  /**
   * public interface to focus on input
   */
  focus = () => {
    this.refs.input && this.refs.input.focus();
  };

  movedOutOfFocus = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { onBlur } = this.props;
    const value = e.target.value;
    this.onChange({
      target: {
        value: value && value.trim(),
      },
    });
    onBlur && onBlur(e);
  };

  /**
   * public interface to set selection on input
   */
  setSelectionRange = (start: string, length: number) => {
    this.refs.input && this.refs.input.setSelectionRange(start, length);
  };

  renderFootnote = () => {
    const props = this.props;
    const input = this.refs.input;

    if (props.noFooter) {
      return null;
    }

    /* FOOTNOTE: A footnote will appear below the input if:
     *   1. Both 'statusType' and 'statusFootnotes' are passed in from the parent,
     *      AND there exists a property on 'statusFootnotes'
     *      matching the 'statusType' passed in.
     *
     *      Example props:
     *         props.statusType = 'warning'
     *         props.statusFootnotes = {
     *             warning: 'Your input may cause issues.'
     *         }
     *         props.footnote = 'This is important to use Branch properly.'
     *
     *       Will display: 'Your input may cause issues.'
     *
     *    2. A 'footnote' prop is passed in, and the above condition is not met.
     *
     *       Example props:
     *          props.statusType = null
     *          props.statusFootnotes = {
     *              warning: 'Your input may cause issues.'
     *          }
     *          props.footnote = 'This is important to use Branch properly.'
     *
     *       Will display: 'This is important to use Branch properly.'
     */
    const statusFootnote = !!props.statusType && !!props.statusFootnotes && props.statusFootnotes[props.statusType];
    const footnote = statusFootnote || props.footnote;

    /* VALIDATION MESSAGE: A validation message will appear ONLY when the Input
     * internally has state.error set to true (if native HTML <input> logic
     * establishes the input is invalid).
     *    - The default HTML validation message will appear in this case, or you
     *      can override with props.validationMessage to display your own message.
     *
     *    - The parent has no way to trigger a validation message to appear.
     *
     *    - You can only suppress this message by passing in props.noFooter as
     *      true, though no footnote will be shown as well.
     *
     * NOTE: Footnotes and Validation Messages are independent - both can appear
     */
    let validationMessage = null;

    if (this.state.error) {
      validationMessage = props.validationMessage || (input && input.validationMessage);
    }

    return (
      <span className="input-control__footer h_d-f" data-testid={`${props.testid}__footer`}>
        {footnote && <span className="input-control__note h_ta-start">{footnote}</span>}
        {validationMessage && <span className="input-control__warning h_ta-end h_d-b h_c-error">{validationMessage}</span>}
      </span>
    );
  };

  render() {
    const { className, inputClassName, testid, customChildComponent, statusType, label } = this.props;
    return (
      <div
        className={classNames('input-control', className, {
          [`input-control--${statusType || ''}`]: !!statusType,
        })}
        data-testid={testid}
      >
        {typeof label === 'string' ? (
          <Title
            label={label}
            description={this.props.description}
            labelClassName={this.props.labelClassName}
            descriptionClassName={this.props.descriptionClassName}
            hoverableTooltip={this.props.hoverableTooltip}
            tooltip={this.props.tooltip}
          />
        ) : (
          label
        )}
        {customChildComponent ? (
          customChildComponent
        ) : (
          <input
            className={classNames('input-control__input h_bdrs-8px h_w-100', inputClassName)}
            {...R.pick(VALID_INPUT_PROPS, this.props)}
            value={this.state.value}
            onKeyPress={this.props.onKeyPress}
            onChange={this.onChange}
            data-testid={`${testid}-input`}
            ref="input"
            onBlur={this.movedOutOfFocus}
          />
        )}
        {this.renderFootnote()}
      </div>
    );
  }
}

export default Input;
